import paper from "paper";
import type { StampMeta } from "./StampMeta";

export interface LineStampPlacement {
  x: number;
  y: number;
  rotation: number; // degrees
  scale: number;
}

/**
 * Distribute a stamp along a Paper.js path.
 *
 * The stamp's natural width (bbox.w * scale) determines its footprint.
 * For closed paths, spacing is adjusted so the seam meets cleanly.
 */
export function placeStampsAlongPath(
  path: paper.Path | paper.CompoundPath,
  stamp: StampMeta,
  options: {
    spacing: number;
    scale: number;
    rotateAlong: boolean;
    closed?: boolean;
  },
): LineStampPlacement[] {
  const { spacing, scale, rotateAlong } = options;
  const stampLen = stamp.bbox.w * scale;
  const step = Math.max(0.001, stampLen + spacing);

  const total = path.length;
  if (total <= 0) return [];

  const closed = options.closed ?? path.closed;
  const placements: LineStampPlacement[] = [];

  let count: number;
  let actualStep: number;

  if (closed && total > 0) {
    count = Math.max(1, Math.round(total / step));
    actualStep = total / count;
  } else {
    count = Math.max(1, Math.floor((total - stampLen) / step) + 1);
    actualStep = step;
  }

  type PathLike = paper.Path & {
    getPointAt: (offset: number) => paper.Point | null;
    getTangentAt: (offset: number) => paper.Point | null;
  };
  const p = path as unknown as PathLike;
  for (let i = 0; i < count; i += 1) {
    const offset = closed ? i * actualStep : i * actualStep + stampLen / 2;
    const at = Math.min(offset, total - 0.0001);
    const pt = p.getPointAt(at);
    if (!pt) continue;
    let rotation = 0;
    if (rotateAlong) {
      const tan = p.getTangentAt(at);
      if (tan) rotation = (Math.atan2(tan.y, tan.x) * 180) / Math.PI;
    }
    placements.push({ x: pt.x, y: pt.y, rotation, scale });
  }

  return placements;
}
